import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Save, Eye, Mail } from 'lucide-react';

import { LogoSiteNameCard } from '@/components/admin/site-settings/LogoSiteNameCard';
import { HeroSectionCard } from '@/components/admin/site-settings/HeroSectionCard';
import { NumberedSectionCard } from '@/components/admin/site-settings/NumberedSectionCard';
import { CtaSectionCard } from '@/components/admin/site-settings/CtaSectionCard';
import { ThemeColorsCard } from '@/components/admin/site-settings/ThemeColorsCard';
import { AboutMeCard } from '@/components/admin/site-settings/AboutMeCard';
import { AnalyticsCard } from '@/components/admin/site-settings/AnalyticsCard';
import { PricingCard } from '@/components/admin/site-settings/PricingCard';

import { API } from '@/lib/apiBase';

export default function AdminSiteSettings() {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const themeCtx = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [imgVersion, setImgVersion] = useState(() => Date.now());
  const logoRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/site-settings`);
        if (!cancelled && res.ok) setSettings(await res.json());
      } catch (e) {
        if (!cancelled) toast.error(t('common.error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/site-settings`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings),
      });
      if (res.ok) {
        const newSettings = await res.json();
        setSettings(newSettings);
        if (themeCtx?.applyTheme) themeCtx.applyTheme(newSettings);
        toast.success(t('siteSettings.saved'));
      } else {
        // Surface backend validation messages (e.g. retention_days <= reminder window)
        let msg = t('common.error');
        try {
          const body = await res.json();
          if (body?.detail) msg = typeof body.detail === 'string' ? body.detail : msg;
        } catch (_) { /* ignore parse errors */ }
        toast.error(msg);
      }
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [settings, getAuthHeaders, themeCtx, t]);

  const handleLogoUpload = useCallback(async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      const res = await fetch(`${API}/admin/site-settings/upload-logo`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: formData,
      });
      if (res.ok) {
        toast.success(t('siteSettings.logoUploaded'));
        setSettings((prev) => ({ ...prev, has_custom_logo: true }));
        setImgVersion(Date.now());
      } else {
        toast.error(t('common.error'));
      }
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setUploadingLogo(false);
    }
  }, [getAuthHeaders, t]);

  const handleLogoRemove = useCallback(async () => {
    setRemovingLogo(true);
    try {
      const res = await fetch(`${API}/admin/site-settings/logo`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (res.ok) {
        toast.success(t('siteSettings.logoRemoved'));
        setSettings((prev) => ({ ...prev, has_custom_logo: false }));
      } else {
        toast.error(t('common.error'));
      }
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setRemovingLogo(false);
    }
  }, [getAuthHeaders, t]);

  const handleHeroUpload = useCallback(async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingHero(true);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      const res = await fetch(`${API}/admin/site-settings/upload-hero`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: formData,
      });
      if (res.ok) {
        toast.success(t('siteSettings.heroUploaded'));
        setSettings((prev) => ({ ...prev, has_custom_hero: true }));
        setImgVersion(Date.now());
      } else {
        toast.error(t('common.error'));
      }
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setUploadingHero(false);
    }
  }, [getAuthHeaders, t]);

  const updateField = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handlePreviewPrintEmail = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/preview-print-email`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        toast.error(t('common.error'));
        return;
      }
      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after the new tab has had a chance to read the blob.
      if (win) setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(t('common.error'));
    }
  }, [getAuthHeaders, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="admin-site-settings">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('siteSettings.title')}</h1>
          <p className="text-muted-foreground">{t('siteSettings.description')}</p>
        </div>
        <div className="flex gap-3">
          <Button asChild variant="outline">
            <a href="/" target="_blank" rel="noopener noreferrer">
              <Eye className="w-4 h-4 mr-2" />
              {t('siteSettings.preview')}
            </a>
          </Button>
          <Button
            variant="outline"
            onClick={handlePreviewPrintEmail}
            data-testid="preview-print-email-btn"
            title={t('siteSettings.previewPrintEmail')}
          >
            <Mail className="w-4 h-4 mr-2" />
            {t('siteSettings.previewPrintEmail')}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {t('common.save')}
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground bg-muted/50 rounded-sm px-4 py-3 mb-8">
        {t('siteSettings.leaveEmpty')}
      </p>

      <div className="space-y-8">
        <LogoSiteNameCard
          settings={settings}
          updateField={updateField}
          logoRef={logoRef}
          uploadingLogo={uploadingLogo}
          removingLogo={removingLogo}
          onLogoUpload={handleLogoUpload}
          onLogoRemove={handleLogoRemove}
          imgVersion={imgVersion}
        />

        <HeroSectionCard
          settings={settings}
          updateField={updateField}
          heroRef={heroRef}
          uploadingHero={uploadingHero}
          onHeroUpload={handleHeroUpload}
          imgVersion={imgVersion}
        />

        <NumberedSectionCard
          settings={settings}
          updateField={updateField}
          keyPrefix="feature"
          sectionLabelKey="siteSettings.featuresSection"
          itemLabelKey="siteSettings.feature"
          titleLabelKey="siteSettings.featureTitle"
          descLabelKey="siteSettings.featureDesc"
        />

        <NumberedSectionCard
          settings={settings}
          updateField={updateField}
          keyPrefix="step"
          sectionLabelKey="siteSettings.stepsSection"
          itemLabelKey="siteSettings.step"
          titleLabelKey="siteSettings.stepTitle"
          descLabelKey="siteSettings.stepDesc"
        />

        <CtaSectionCard settings={settings} updateField={updateField} />

        <ThemeColorsCard settings={settings} updateField={updateField} />

        <AboutMeCard
          settings={settings}
          updateField={updateField}
          imgVersion={imgVersion}
          onPhotoUploaded={() => setImgVersion(Date.now())}
        />

        <AnalyticsCard settings={settings} updateField={updateField} />

        <PricingCard settings={settings} updateField={updateField} />

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary/90"
            data-testid="save-settings-bottom-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
